import { CgDocType } from '../ocr/ocrResponseTypes';

/**
 * Labels UI utilisateur par type de document.
 * Affichés dans le wizard (titre, sous-titre, instruction dropzone).
 */
export interface DocumentLabel {
  title: string;
  subtitle: string;
  dropzoneText: string;
  helper?: string;
}

export const DOCUMENT_LABELS: Record<CgDocType, DocumentLabel> = {
  carte_grise: {
    title: 'Carte grise',
    subtitle: 'avec la mention "cédée le .../.../..." et la signature du titulaire',
    dropzoneText: 'Déposez la carte grise ici',
    helper: 'Si co-titulaire, sa signature est également requise.',
  },
  cerfa_cession: {
    title: 'Certificat de cession',
    subtitle: "Cerfa 15776*01 complété par le vendeur et l'acquéreur",
    dropzoneText: 'Déposez le certificat de cession ici',
  },
  cerfa_demande_immat: {
    title: "Demande d'immatriculation",
    subtitle: "Cerfa 13750 (*07 courant, *05 accepté) de demande de certificat d'immatriculation",
    dropzoneText: 'Déposez le Cerfa 13750 (*05 ou *07) ici',
  },
  mandat: {
    title: "Mandat d'immatriculation",
    subtitle: 'Cerfa 13757*03 complété au nom de SKIPCAR (SIRET 899 182 182 000 27)',
    dropzoneText: 'Déposez le mandat ici',
    helper: 'Le mandat doit impérativement être au nom de SKIPCAR.',
  },
  assurance: {
    title: "Attestation d'assurance",
    subtitle: "Attestation, carte verte ou mémo avec date d'effet du contrat",
    dropzoneText: "Déposez l'attestation d'assurance ici",
    helper: "Les devis ne sont pas acceptés. L'immatriculation (ou VIN) et la date d'effet sont obligatoires.",
  },
  controle_technique: {
    title: 'Contrôle technique',
    subtitle: 'Favorable < 6 mois ou non-favorable < 2 mois (si véhicule de plus de 4 ans)',
    dropzoneText: 'Déposez le contrôle technique ici',
  },
  piece_identite: {
    title: "Pièce d'identité du nouveau titulaire — RECTO",
    subtitle: 'CNI, passeport français ou carte de séjour (face photo)',
    dropzoneText: "Déposez la pièce d'identité ici",
  },
  piece_identite_verso: {
    title: "Pièce d'identité du nouveau titulaire — VERSO",
    subtitle: 'Face arrière de la CNI / carte de séjour du titulaire',
    dropzoneText: "Déposez le verso de la pièce d'identité ici",
  },
  piece_identite_hebergeur: {
    title: "Pièce d'identité de l'hébergeur — RECTO",
    subtitle: 'Mode hébergement — CNI ou passeport de la personne qui héberge',
    dropzoneText: "Déposez la pièce d'identité de l'hébergeur ici",
    helper: "Le slot apparaît automatiquement quand 2 CNI sont détectées + attestation d'hébergement présente.",
  },
  piece_identite_hebergeur_verso: {
    title: "Pièce d'identité de l'hébergeur — VERSO",
    subtitle: "Face arrière de la pièce d'identité de l'hébergeur",
    dropzoneText: "Déposez le verso de la pièce d'identité de l'hébergeur ici",
  },
  piece_identite_cotitulaire: {
    title: "Pièce d'identité du co-titulaire — RECTO",
    subtitle: 'CNI, passeport ou carte de séjour du co-titulaire acquéreur (face photo)',
    dropzoneText: "Déposez la pièce d'identité du co-titulaire ici",
    helper: 'Le slot apparaît quand un co-titulaire acquéreur est déclaré. Déposez bien les DEUX faces.',
  },
  piece_identite_cotitulaire_verso: {
    title: "Pièce d'identité du co-titulaire — VERSO",
    subtitle: "Face arrière de la pièce d'identité du co-titulaire acquéreur",
    dropzoneText: "Déposez le verso de la pièce d'identité du co-titulaire ici",
  },
  permis_conduire: {
    title: 'Permis de conduire du titulaire — RECTO',
    subtitle: 'Face identité (photo), en cours de validité',
    dropzoneText: 'Déposez le permis de conduire ici',
  },
  permis_conduire_verso: {
    title: 'Permis de conduire du titulaire — VERSO',
    subtitle: 'Face catégories / validités du permis du titulaire',
    dropzoneText: 'Déposez le verso du permis de conduire ici',
  },
  permis_conduire_cotitulaire: {
    title: 'Permis de conduire du co-titulaire — RECTO',
    subtitle: 'Face identité (photo) du permis du co-titulaire acquéreur',
    dropzoneText: 'Déposez le permis du co-titulaire ici',
  },
  permis_conduire_cotitulaire_verso: {
    title: 'Permis de conduire du co-titulaire — VERSO',
    subtitle: 'Face catégories / validités du permis du co-titulaire',
    dropzoneText: 'Déposez le verso du permis du co-titulaire ici',
  },
  kbis: {
    title: 'Extrait Kbis',
    subtitle: 'Pour les personnes morales uniquement',
    dropzoneText: "Déposez l'extrait Kbis ici",
  },
  // Deux emplacements distincts : un dossier peut compter deux sociétés, et un
  // slot unique faisait que le second extrait écrasait le premier.
  kbis_acheteur: {
    title: "Extrait Kbis de l'acquéreur",
    subtitle: 'Identifie et domicilie la société qui achète',
    dropzoneText: "Déposez l'extrait Kbis de l'acquéreur ici",
  },
  kbis_vendeur: {
    title: 'Extrait Kbis du vendeur',
    subtitle: 'Facultatif — pré-remplit la raison sociale et le SIRET du vendeur',
    dropzoneText: "Déposez l'extrait Kbis du vendeur ici",
  },
  justif_domicile: {
    title: 'Justificatif de domicile',
    subtitle: 'Facture EDF/gaz/eau, téléphone, impôts, loyer ou assurance habitation — de moins de 6 mois',
    dropzoneText: 'Déposez le justificatif de domicile ici',
  },
  attestation_hebergement: {
    title: "Attestation d'hébergement",
    subtitle: "Attestation sur l'honneur de l'hébergeant",
    dropzoneText: "Déposez l'attestation d'hébergement ici",
  },
  recepisse_da: {
    title: "Récépissé de déclaration d'achat",
    subtitle: 'Parcours A — quand le vendeur est un professionnel intermédiaire',
    dropzoneText: "Déposez le récépissé de déclaration d'achat ici",
    helper: "La date ET l'heure d'achat doivent être antérieures à la cession au client final.",
  },
  acte_deces: {
    title: 'Acte de décès',
    subtitle: 'Succession — acte ou certificat de décès du titulaire de la carte grise',
    dropzoneText: "Déposez l'acte de décès ici",
    helper: 'Le nom du défunt doit correspondre au titulaire (C.1) de la carte grise.',
  },
  justif_succession: {
    title: 'Justificatif de succession',
    subtitle: 'Succession — acte de notoriété (notaire) OU attestation signée de tous les héritiers',
    dropzoneText: 'Déposez le justificatif de succession ici',
    helper: "Acte de notoriété notarié, ou attestation sur l'honneur désignant l'héritier qui récupère le véhicule.",
  },
};
